import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus, EventType } from "@azure/msal-browser";
import { loginRequest } from "../lib/msalConfig";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthed = useIsAuthenticated();
    const [user, setUser] = useState(undefined); // undefined = loading, null = not signed in
    const [authError, setAuthError] = useState("");

    // Keep an active account selected
    useEffect(() => {
        if (accounts.length > 0 && !instance.getActiveAccount()) {
            instance.setActiveAccount(accounts[0]);
        }
    }, [accounts, instance]);

    // Handle login/logout events from MSAL
    useEffect(() => {
        const id = instance.addEventCallback((evt) => {
            if (evt.eventType === EventType.LOGIN_SUCCESS && evt.payload?.account) {
                instance.setActiveAccount(evt.payload.account);
            }
            if (evt.eventType === EventType.LOGIN_FAILURE) {
                setAuthError(evt.error?.errorMessage || "Sign-in failed");
            }
        });
        return () => {
            if (id) instance.removeEventCallback(id);
        };
    }, [instance]);

    // Whenever auth state changes, hit /api/auth/me to get backend role + provisioning
    const refresh = useCallback(async () => {
        if (!isAuthed) {
            setUser(null);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Auth check failed";
            setAuthError(typeof msg === "string" ? msg : "Auth check failed");
            setUser(null);
            return null;
        }
    }, [isAuthed]);

    useEffect(() => {
        if (inProgress === InteractionStatus.None) {
            refresh();
        }
    }, [inProgress, isAuthed, refresh]);

    const login = useCallback(async () => {
        setAuthError("");
        try {
            await instance.loginRedirect(loginRequest);
        } catch (e) {
            setAuthError(e?.errorMessage || e?.message || "Sign-in failed");
        }
    }, [instance]);

    const logout = useCallback(async () => {
        const account = instance.getActiveAccount();
        await instance.logoutRedirect({
            account,
            postLogoutRedirectUri: window.location.origin,
        });
    }, [instance]);

    const loading = inProgress !== InteractionStatus.None || user === undefined;

    return (
        <AuthContext.Provider value={{ user, loading, authError, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
